import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import type { ScannerConfigRow } from '@tennis-scanner/shared';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

// Tenant-wide scanner config — controls how the scanner-worker hunts
// opportunities. Admin-only mutation; non-admins see the form disabled
// (the role gate is enforced at the route level too).

interface Props {
  current: ScannerConfigRow;
  isAdmin: boolean;
}

interface DraftState {
  minEdgePct: string;
  cooldownSec: string;
  cooldownBreakEdgeDeltaPct: string;
  prematchMaxStalenessSec: string;
  liveMaxStalenessSec: string;
  weakenedToClosedMin: string;
  minSecondsBeforeMatch: string;
  minConfidence: string;
}

function fromConfig(cfg: ScannerConfigRow): DraftState {
  return {
    minEdgePct: cfg.minEdgePct.toString(),
    cooldownSec: cfg.cooldownSec.toString(),
    cooldownBreakEdgeDeltaPct: cfg.cooldownBreakEdgeDeltaPct.toString(),
    prematchMaxStalenessSec: cfg.prematchMaxStalenessSec.toString(),
    liveMaxStalenessSec: cfg.liveMaxStalenessSec.toString(),
    weakenedToClosedMin: cfg.weakenedToClosedMin.toString(),
    minSecondsBeforeMatch: cfg.minSecondsBeforeMatch.toString(),
    minConfidence: cfg.minConfidence === null ? '' : cfg.minConfidence.toString(),
  };
}

export function ScannerConfigForm({ current, isAdmin }: Props) {
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState<DraftState>(() => fromConfig(current));
  // Keep in sync with the latest server value while the user isn't editing.
  useEffect(() => {
    setDraft(fromConfig(current));
  }, [current]);

  const update = trpc.config.update.useMutation({
    onSuccess: async () => {
      toast.success('Scanner config actualizado');
      await utils.config.get.invalidate();
    },
    onError: (err) => toast.error('No se pudo guardar', { description: err.message }),
  });

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!isAdmin) return;
    const minConf = draft.minConfidence.trim();
    update.mutate({
      minEdgePct: Number.parseFloat(draft.minEdgePct),
      cooldownSec: Number.parseInt(draft.cooldownSec, 10),
      cooldownBreakEdgeDeltaPct: Number.parseFloat(draft.cooldownBreakEdgeDeltaPct),
      prematchMaxStalenessSec: Number.parseInt(draft.prematchMaxStalenessSec, 10),
      liveMaxStalenessSec: Number.parseInt(draft.liveMaxStalenessSec, 10),
      weakenedToClosedMin: Number.parseInt(draft.weakenedToClosedMin, 10),
      minSecondsBeforeMatch: Number.parseInt(draft.minSecondsBeforeMatch, 10),
      minConfidence: minConf === '' ? null : Number.parseFloat(minConf),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      {!isAdmin && (
        <Alert variant="info">
          <AlertTitle>Sólo lectura</AlertTitle>
          <AlertDescription>
            Sólo admins pueden modificar la config del scanner. Pedí promoción si lo necesitás.
          </AlertDescription>
        </Alert>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <Field
          id="cfg-edge"
          label="Min edge (0..1, ej 0.03 = 3%)"
          step="0.001"
          value={draft.minEdgePct}
          disabled={!isAdmin}
          onChange={(v) => setDraft({ ...draft, minEdgePct: v })}
        />
        <Field
          id="cfg-cooldown"
          label="Cooldown (s)"
          step="1"
          value={draft.cooldownSec}
          disabled={!isAdmin}
          onChange={(v) => setDraft({ ...draft, cooldownSec: v })}
        />
        <Field
          id="cfg-break"
          label="Cooldown break Δedge"
          step="0.001"
          value={draft.cooldownBreakEdgeDeltaPct}
          disabled={!isAdmin}
          onChange={(v) => setDraft({ ...draft, cooldownBreakEdgeDeltaPct: v })}
        />
        <Field
          id="cfg-pmstale"
          label="Prematch staleness max (s)"
          step="1"
          value={draft.prematchMaxStalenessSec}
          disabled={!isAdmin}
          onChange={(v) => setDraft({ ...draft, prematchMaxStalenessSec: v })}
        />
        <Field
          id="cfg-livestale"
          label="Live staleness max (s)"
          step="1"
          value={draft.liveMaxStalenessSec}
          disabled={!isAdmin}
          onChange={(v) => setDraft({ ...draft, liveMaxStalenessSec: v })}
        />
        <Field
          id="cfg-weakened"
          label="Weakened → closed (min)"
          step="1"
          value={draft.weakenedToClosedMin}
          disabled={!isAdmin}
          onChange={(v) => setDraft({ ...draft, weakenedToClosedMin: v })}
        />
        <Field
          id="cfg-prekick"
          label="Min seconds before match"
          step="1"
          value={draft.minSecondsBeforeMatch}
          disabled={!isAdmin}
          onChange={(v) => setDraft({ ...draft, minSecondsBeforeMatch: v })}
        />
        <Field
          id="cfg-conf"
          label="Min confidence (0..1, vacío = sin gate)"
          step="0.01"
          value={draft.minConfidence}
          disabled={!isAdmin}
          onChange={(v) => setDraft({ ...draft, minConfidence: v })}
        />
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={!isAdmin || update.isPending}>
          {update.isPending ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>
    </form>
  );
}

function Field({
  id,
  label,
  step,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  step: string;
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
